/**
 * Bug reports and change requests.
 *
 * There is no issue tracker a visitor can reach, so the address is the only way
 * in and it has to be on the landing page rather than buried in the footer. The
 * address is offered as a link and as a copy button, because a browser without a
 * configured mail client makes the link do nothing and the visitor then has no
 * way to act on it.
 */

import { useState } from 'react'
import { Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { copyText } from '../../lib/clipboard'

/** Address every report and suggestion goes to. */
export const CONTACT_EMAIL = 'contact@chilichill.fans'

export function ContactCard() {
  const { t } = useTranslation()
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')

  async function copy() {
    setCopyState((await copyText(CONTACT_EMAIL)) ? 'copied' : 'failed')
  }

  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardHeader className="flex-row items-center gap-2">
        <Mail className="text-primary size-4" aria-hidden />
        <CardTitle>{t('home.contactTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <CardDescription>{t('home.contactBody')}</CardDescription>
        <div className="flex flex-wrap items-center gap-2">
          <a
            className="bg-background rounded-md border px-2.5 py-1 font-mono text-sm hover:underline"
            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t('home.contactSubject'))}`}
          >
            {CONTACT_EMAIL}
          </a>
          <Button variant="outline" size="sm" onClick={() => void copy()}>
            {copyState === 'copied' ? t('common.copied') : t('home.contactCopy')}
          </Button>
        </div>
        {copyState === 'failed' ? (
          <p className="text-destructive text-xs">{t('export.copyFailed')}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}
